/**
 * workerStubGenerator.js — produces ready-to-run worker code stubs for
 * a worker_task node, in five languages. The Worker tab on
 * NodePropertiesPanel calls these to populate its language tabs.
 *
 * Why generate, not document?
 * ───────────────────────────
 * Conductor OSS own UI shows a static "Sample worker code" block per language
 * (visible in the screenshot the user shared). That's table stakes. The
 * differentiator is that we generate stubs grounded in *this specific
 * node's* metadata — taskDefName, inputKeys, outputKeys, retry/timeout
 * config — so the user pastes one file into their worker project and
 * the @WorkerTask name + parameter shape match what the workflow
 * expects. No translation step.
 *
 * Conductor-poll vs Agentlets-managed (Temporal OSS):
 * ───────────────────────────────────────────────
 * For now ALL stubs target the Conductor SDK polling pattern. When
 * `hostingMode === 'agentlets_managed'` we still emit the Conductor
 * SDK shape but point CONDUCTOR_SERVER_URL at the agentlets endpoint
 * — same on-the-wire protocol, different fleet. A future iteration may
 * also offer a Temporal OSS SDK-shaped stub for users who want to write
 * activity code directly. Tracked under task #20 layer-2.
 *
 * Languages:
 *   java     — @WorkerTask annotation + TaskHandler bootstrap
 *   python   — @WorkerTask decorator + TaskHandler.start_processes
 *   javascript — TaskRunner / TaskWorker pattern (Conductor OSS JavaScript SDK)
 *   go       — taskrunner.WorkerHost
 *   csharp   — [WorkerTask] attribute (Conductor OSS .NET client)
 *
 * Each stub includes:
 *   1. Import block
 *   2. Worker class / function with @WorkerTask binding to the
 *      node's taskDefName
 *   3. Typed input parameters drawn from `inputKeys`
 *   4. Output object containing `outputKeys`
 *   5. Bootstrap (main / __main__ / TaskHandler.start) wired to the
 *      env-var connection config
 *   6. Comment header with the env vars the worker process needs
 */

const SUPPORTED_LANGUAGES = ['java', 'python', 'javascript', 'go', 'csharp'];

/**
 * Best-effort parse of the optional `inputKeys` / `outputKeys` config
 * fields, which arrive either as a JSON-encoded string (the form
 * field) or as a parsed array (already-saved data).
 */
function parseKeys(raw) {
  if (Array.isArray(raw)) return raw.filter((k) => typeof k === 'string' && k.length > 0);
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((k) => typeof k === 'string' && k.length > 0);
  } catch (_) {
    // Fall through — accept comma-separated as a friendly fallback
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Convert a snake_case / camelCase / kebab-case string into PascalCase
 * for class names. Defensive against empty + numeric-leading inputs.
 */
function pascalCase(s) {
  const cleaned = String(s || 'WorkerTask').replace(/[^a-zA-Z0-9]+/g, ' ').trim();
  if (!cleaned) return 'WorkerTask';
  const parts = cleaned.split(/\s+/).map((p) => p.charAt(0).toUpperCase() + p.slice(1));
  let out = parts.join('');
  if (/^[0-9]/.test(out)) out = `Task${out}`;
  return out;
}

/** snake_case for python task-def names. */
function snakeCase(s) {
  return String(s || 'worker_task')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .toLowerCase()
    .replace(/^_+|_+$/g, '') || 'worker_task';
}

/**
 * Pull all the worker-shape fields off a worker_task node into a flat
 * shape the language emitters can consume. Keeps the language
 * emitters dumb — they don't have to know about the soarNodes config
 * schema.
 */
function extractWorkerShape(node) {
  const cfg = (node && node.config) || {};
  const taskDefName = cfg.taskDefName || (node && node.id) || 'worker_task';
  return {
    taskDefName,
    inputKeys:  parseKeys(cfg.inputKeys),
    outputKeys: parseKeys(cfg.outputKeys),
    retryCount:           cfg.retryCount ?? 3,
    retryLogic:           cfg.retryLogic || 'EXPONENTIAL_BACKOFF',
    retryDelaySeconds:    cfg.retryDelaySeconds ?? 30,
    timeoutSeconds:       cfg.timeoutSeconds ?? 1800,
    responseTimeoutSeconds: cfg.responseTimeoutSeconds ?? 600,
    hostingMode:          cfg.hostingMode || 'conductor_poll',
  };
}

// ── Language emitters ───────────────────────────────────────────────

function emitJava(shape) {
  const className = pascalCase(shape.taskDefName) + 'Worker';
  const inputParams = shape.inputKeys.length > 0
    ? shape.inputKeys.map((k) => `      @InputParam("${k}") String ${k}`).join(',\n')
    : '      // No input keys declared. Add @InputParam("name") String name to taste.';
  const outputBody = shape.outputKeys.length > 0
    ? shape.outputKeys.map((k) => `    output.put("${k}", /* TODO: compute */ null);`).join('\n')
    : '    // TODO: populate output map with whatever your downstream nodes consume.\n    output.put("result", "ok");';
  return `// ${className}.java
//
// Worker for taskDefName="${shape.taskDefName}".
//
// Run this against:
//   CONDUCTOR_SERVER_URL  — e.g. https://agentlets.your-tenant.com/api
//   CONDUCTOR_AUTH_KEY    — generated in Settings → Applications
//   CONDUCTOR_AUTH_SECRET — generated in Settings → Applications
//
// Maven (pom.xml):
//   <dependency>
//     <groupId>org.conductoross</groupId>
//     <artifactId>conductor-client-spring</artifactId>
//     <version>3.21.10</version>
//   </dependency>
package com.example.workers;

import java.util.HashMap;
import java.util.Map;
import com.netflix.conductor.client.automator.TaskRunnerConfigurer;
import com.netflix.conductor.client.http.TaskClient;
import com.netflix.conductor.sdk.workflow.task.InputParam;
import com.netflix.conductor.sdk.workflow.task.WorkerTask;
import com.netflix.conductor.sdk.workflow.executor.WorkflowExecutor;

public class ${className} {

  /** Body of the worker. Conductor invokes this each time it pulls a
   *  task off the queue under taskDefName="${shape.taskDefName}". */
  @WorkerTask(value = "${shape.taskDefName}", pollingInterval = 200, threadCount = 5)
  public Map<String, Object> run(
${inputParams}
  ) {
    Map<String, Object> output = new HashMap<>();
${outputBody}
    return output;
  }

  /** Bootstrap. Run this once when the worker process starts. */
  public static void main(String[] args) {
    String serverUrl  = System.getenv().getOrDefault("CONDUCTOR_SERVER_URL", "http://localhost:8080/api");
    String authKey    = System.getenv("CONDUCTOR_AUTH_KEY");
    String authSecret = System.getenv("CONDUCTOR_AUTH_SECRET");

    WorkflowExecutor executor = new WorkflowExecutor(serverUrl);
    if (authKey != null && authSecret != null) {
      executor.getApiClient().setUsername(authKey);
      executor.getApiClient().setPassword(authSecret);
    }

    // Scan this package for every @WorkerTask-annotated method and
    // start polling. Add additional packages as your worker fleet grows.
    executor.initWorkers("com.example.workers");
    System.out.println("Worker '${shape.taskDefName}' polling " + serverUrl + "…");
  }
}
`;
}

function emitPython(shape) {
  const fnName = snakeCase(shape.taskDefName);
  const inputArgs = shape.inputKeys.length > 0
    ? shape.inputKeys.map((k) => `${k}: object`).join(', ')
    : '*args, **kwargs';
  const outputBody = shape.outputKeys.length > 0
    ? shape.outputKeys.map((k) => `    "${k}": None,  # TODO`).join('\n')
    : '    "result": "ok",  # TODO: populate fields downstream nodes will consume';
  return `# ${fnName}_worker.py
#
# Worker for task_definition_name="${shape.taskDefName}".
#
# pip install conductor-python
#
# Env vars:
#   CONDUCTOR_SERVER_URL   e.g. https://agentlets.your-tenant.com/api
#   CONDUCTOR_AUTH_KEY     generated in Settings → Applications
#   CONDUCTOR_AUTH_SECRET  generated in Settings → Applications

import os

from conductor.client.automator.task_handler import TaskHandler
from conductor.client.configuration.configuration import Configuration
from conductor.client.configuration.settings.authentication_settings import AuthenticationSettings
from conductor.client.worker.worker_task import WorkerTask


@WorkerTask(
    task_definition_name='${shape.taskDefName}',
    poll_interval=200.0,
    domain=None,  # set to e.g. 'prod' to scope this worker to a domain
)
def ${fnName}(${inputArgs}) -> dict:
    """Body of the worker. Conductor invokes this each time it pulls a
    task off the queue under task_definition_name='${shape.taskDefName}'.
    """
    return {
${outputBody}
    }


def main() -> None:
    cfg = Configuration(
        server_api_url=os.getenv('CONDUCTOR_SERVER_URL', 'http://localhost:8080/api'),
        authentication_settings=AuthenticationSettings(
            key_id=os.getenv('CONDUCTOR_AUTH_KEY', ''),
            key_secret=os.getenv('CONDUCTOR_AUTH_SECRET', ''),
        ) if os.getenv('CONDUCTOR_AUTH_KEY') else None,
    )
    handler = TaskHandler(configuration=cfg, scan_for_annotated_workers=True)
    handler.start_processes()
    print(f"Worker '${shape.taskDefName}' polling {cfg.server_api_url}…")
    handler.join_processes()


if __name__ == '__main__':
    main()
`;
}

function emitJavaScript(shape) {
  const inputDestructure = shape.inputKeys.length > 0
    ? `      const { ${shape.inputKeys.join(', ')} } = task.inputData || {};`
    : '      const inputData = task.inputData || {};';
  const outputBody = shape.outputKeys.length > 0
    ? shape.outputKeys.map((k) => `          ${k}: null, // TODO`).join('\n')
    : '          result: "ok",';
  return `// ${shape.taskDefName}-worker.js
//
// Worker for taskDefName="${shape.taskDefName}".
//
// Uses the Conductor OSS JavaScript SDK
// (see https://github.com/conductor-oss for the current published package).
//
// Env vars:
//   CONDUCTOR_SERVER_URL  e.g. https://agentlets.your-tenant.com/api
//   CONDUCTOR_AUTH_KEY    generated in Settings → Applications
//   CONDUCTOR_AUTH_SECRET generated in Settings → Applications

const {
  conductorClient,
  TaskRunner,
} = require('conductor-javascript');

async function main() {
  const client = await conductorClient({
    keyId:    process.env.CONDUCTOR_AUTH_KEY,
    keySecret: process.env.CONDUCTOR_AUTH_SECRET,
    serverUrl: process.env.CONDUCTOR_SERVER_URL || 'http://localhost:8080/api',
  });

  const worker = {
    taskDefName: '${shape.taskDefName}',
    pollInterval: 200,
    execute: async (task) => {
${inputDestructure}
      // TODO: implement the worker body.
      return {
        outputData: {
${outputBody}
        },
        status: 'COMPLETED',
      };
    },
  };

  const runner = new TaskRunner({
    taskResource:        client.taskResource,
    options:             { pollInterval: 200, domain: undefined, concurrency: 5 },
    logger:              console,
    worker,
  });
  runner.startPolling();
  console.log(\`Worker '${shape.taskDefName}' polling \${process.env.CONDUCTOR_SERVER_URL}…\`);
}

main().catch((err) => {
  console.error('Worker failed to start:', err);
  process.exit(1);
});
`;
}

function emitGo(shape) {
  const outputBody = shape.outputKeys.length > 0
    ? shape.outputKeys.map((k) => `\t\t\t"${k}": nil, // TODO`).join('\n')
    : '\t\t\t"result": "ok",';
  return `// ${shape.taskDefName}_worker.go
//
// Worker for taskDefName="${shape.taskDefName}".
//
// go get github.com/conductor-sdk/conductor-go
//
// Env vars:
//   CONDUCTOR_SERVER_URL  e.g. https://agentlets.your-tenant.com/api
//   KEY                   generated in Settings → Applications
//   SECRET                generated in Settings → Applications
package main

import (
\t"fmt"
\t"os"

\t"github.com/conductor-sdk/conductor-go/sdk/client"
\t"github.com/conductor-sdk/conductor-go/sdk/model"
\t"github.com/conductor-sdk/conductor-go/sdk/settings"
\t"github.com/conductor-sdk/conductor-go/sdk/worker"
)

func ${pascalCase(shape.taskDefName)}Worker(t *model.Task) (interface{}, error) {
\t// task.InputData is map[string]interface{}.
\t_ = t.InputData
\treturn map[string]interface{}{
${outputBody}
\t}, nil
}

func main() {
\tapiClient := client.NewAPIClient(
\t\tsettings.NewAuthenticationSettings(
\t\t\tos.Getenv("KEY"),
\t\t\tos.Getenv("SECRET"),
\t\t),
\t\tsettings.NewHttpSettings(os.Getenv("CONDUCTOR_SERVER_URL")),
\t)

\ttaskRunner := worker.NewTaskRunnerWithApiClient(apiClient)
\tif err := taskRunner.StartWorker("${shape.taskDefName}", ${pascalCase(shape.taskDefName)}Worker, 5, 200); err != nil {
\t\tfmt.Println("worker failed to start:", err)
\t\tos.Exit(1)
\t}

\tfmt.Printf("Worker '%s' polling %s…\\n", "${shape.taskDefName}", os.Getenv("CONDUCTOR_SERVER_URL"))
\ttaskRunner.WaitWorkers()
}
`;
}

function emitCSharp(shape) {
  const className = pascalCase(shape.taskDefName) + 'Worker';
  const outputBody = shape.outputKeys.length > 0
    ? shape.outputKeys.map((k) => `            { "${k}", null /* TODO */ },`).join('\n')
    : '            { "result", "ok" },';
  return `// ${className}.cs
//
// Worker for taskDefName="${shape.taskDefName}".
//
// dotnet add package Conductor.Client
//
// Env vars:
//   CONDUCTOR_SERVER_URL  e.g. https://agentlets.your-tenant.com/api
//   CONDUCTOR_AUTH_KEY    generated in Settings → Applications
//   CONDUCTOR_AUTH_SECRET generated in Settings → Applications
using System;
using System.Collections.Generic;
using System.Threading.Tasks;
using Conductor.Client.Worker;
using Conductor.Client;

namespace Workers
{
    public class ${className}
    {
        [WorkerTask(taskDefinitionName: "${shape.taskDefName}", pollIntervalMs: 200, workerId: "${className}-1")]
        public Task<Dictionary<string, object>> Run(Dictionary<string, object> input)
        {
            // TODO: implement the worker body.
            var output = new Dictionary<string, object>
            {
${outputBody}
            };
            return Task.FromResult(output);
        }

        public static void Main(string[] args)
        {
            var url    = Environment.GetEnvironmentVariable("CONDUCTOR_SERVER_URL") ?? "http://localhost:8080/api";
            var key    = Environment.GetEnvironmentVariable("CONDUCTOR_AUTH_KEY") ?? "";
            var secret = Environment.GetEnvironmentVariable("CONDUCTOR_AUTH_SECRET") ?? "";

            var host = new WorkerHostBuilder()
                .UseConfiguration(new Configuration { BasePath = url, KeyId = key, KeySecret = secret })
                .RegisterWorkers(typeof(${className}).Assembly)
                .Build();
            host.Start();
            Console.WriteLine($"Worker '${shape.taskDefName}' polling {url}…");
            host.WaitForShutdown();
        }
    }
}
`;
}

const EMITTERS = {
  java: emitJava,
  python: emitPython,
  javascript: emitJavaScript,
  go: emitGo,
  csharp: emitCSharp,
};

/**
 * Generate a worker code stub for a worker_task node in the chosen
 * language. Returns the stub as a string, or `null` if the inputs are
 * malformed (caller should render an empty-state instead).
 *
 * @param {Object} node     a canvas node with type === 'worker_task'
 * @param {string} language one of SUPPORTED_LANGUAGES
 * @returns {string|null}
 */
export function generateWorkerStub(node, language) {
  if (!node || typeof node !== 'object') return null;
  const lang = String(language || '').toLowerCase();
  if (!SUPPORTED_LANGUAGES.includes(lang)) return null;
  const shape = extractWorkerShape(node);
  return EMITTERS[lang](shape);
}

/**
 * Returns the file extension a generated stub should be saved under,
 * for the "Copy as file" / "Download" affordances.
 */
export function fileExtensionFor(language) {
  switch (String(language || '').toLowerCase()) {
    case 'java':       return 'java';
    case 'python':     return 'py';
    case 'javascript': return 'js';
    case 'go':         return 'go';
    case 'csharp':     return 'cs';
    default:           return 'txt';
  }
}

/**
 * The languages this generator knows about. Useful for the language
 * picker in the Worker tab.
 */
export const WORKER_STUB_LANGUAGES = Object.freeze([
  { value: 'java',       label: 'Java',       comment: '@WorkerTask annotation' },
  { value: 'python',     label: 'Python',     comment: '@WorkerTask decorator' },
  { value: 'javascript', label: 'JavaScript', comment: 'TaskRunner / Node.js' },
  { value: 'go',         label: 'Go',         comment: 'taskrunner.WorkerHost' },
  { value: 'csharp',     label: 'C#',         comment: '[WorkerTask] attribute' },
]);
